import * as _ from "lodash";

import store from "../store";
import {
  CustomerGroupFilterState,
  CustomerGroupFilterStateForQuery,
  ErrorPayload,
} from "../types/index";

export function parseJsonDates<
  T extends {
    createdAt?: any;
    lastUpdateAt?: any;
    lastModificationAt?: any;
  }
>(
  obj: T
): T & {
  createdAt: Date;
  lastUpdateAt: Date;
  lastModificationAt: Date;
} {
  const now = new Date(Date.now());
  const createdAt = obj.createdAt ? new Date(obj.createdAt) : now;
  const lastUpdateAt = obj.lastUpdateAt ? new Date(obj.lastUpdateAt) : now;
  const lastModificationAt = obj.lastModificationAt
    ? new Date(obj.lastModificationAt)
    : lastUpdateAt > createdAt
    ? lastUpdateAt
    : createdAt;

  return {
    ...obj,
    createdAt,
    lastUpdateAt,
    lastModificationAt,
  };
}

export async function handleResponse2<T>(response: Response): Promise<T> {
  let dispatchError = false;
  if (!response.ok) {
    if (response.status === 401) {
      return Promise.reject(401);
    } else if (response.status === 500) {
      dispatchError = true;
    } else {
      return Promise.reject(500);
    }
  }
  const body = await response.json();
  if (dispatchError) {
    if (body.error) {
      store.dispatch.errors.newError(body.error);
    }
    return Promise.reject(500);
  }
  if (body.data !== undefined && Array.isArray(body.data)) {
    return body.data.map((item: any) => parseJsonDates<T>(item));
  } else {
    return parseJsonDates<T>(body);
  }
}

export async function handleResponse<T = any>(response: Response): Promise<T> {
  let dispatchError = false;
  if (!response.ok) {
    if (response.status === 401) {
      return Promise.reject(401);
    } else if (response.status === 500) {
      dispatchError = true;
    } else {
      return Promise.reject();
    }
  }
  const body = (await response.json()) as T & { error?: ErrorPayload };
  if (dispatchError) {
    if (body.error) {
      store.dispatch.errors.newError(body.error);
    }
    return Promise.reject(500);
  }
  return Promise.resolve(body);
}

// Differentiation between queries for saving customer groups and other queries is just a temporary fix until all queries
// use the customerGroupFilter directly.
export const getCustomerGroupFilterForQuery = (
  customerGroupFilter: CustomerGroupFilterState,
  opts: {
    saveCustomerGroup?: boolean;
    omit?: (keyof CustomerGroupFilterState)[];
  }
): Partial<CustomerGroupFilterStateForQuery> | CustomerGroupFilterState => {
  const filterForQueryCustomerGroupsSave: CustomerGroupFilterState = {
    activeChurnedType: customerGroupFilter.activeChurnedType,
    ageGroupType: customerGroupFilter.ageGroupType,
    complaintType: customerGroupFilter.complaintType,
    customerAddressPostalCodes: customerGroupFilter.customerAddressPostalCodes,
    customerDuration: customerGroupFilter.customerDuration,
    customerType: customerGroupFilter.customerType,
    electricityFilter: customerGroupFilter.electricityFilter,
    gasFilter: customerGroupFilter.gasFilter,
    heatingFilter: customerGroupFilter.heatingFilter,
    glassFiberFilter: customerGroupFilter.glassFiberFilter,
    maxChurn: customerGroupFilter.maxChurn,
    maxChurnDelta: customerGroupFilter.maxChurnDelta,
    maxClv: customerGroupFilter.maxClv,
    minChurn: customerGroupFilter.minChurn,
    minChurnDelta: customerGroupFilter.minChurnDelta,
    minClv: customerGroupFilter.minClv,
    nextBestActivityFilter: customerGroupFilter.nextBestActivityFilter,
    polygonMapSelection: customerGroupFilter.polygonMapSelection,
    optIn: customerGroupFilter.optIn,
    phone: customerGroupFilter.phone,
    email: customerGroupFilter.email,
    salutation: customerGroupFilter.salutation,
    housingSituationsFilter: customerGroupFilter.housingSituationsFilter,
    filterGroupsEnabled: customerGroupFilter.filterGroupsEnabled,
  };

  const filterForQuery: CustomerGroupFilterStateForQuery = {
    active_churned_type: customerGroupFilter.activeChurnedType,
    age_group_type: customerGroupFilter.ageGroupType,
    complaint_type: customerGroupFilter.complaintType,
    customer_address_postal_codes: customerGroupFilter.customerAddressPostalCodes.map(
      (code) => code.value
    ),
    customer_duration: customerGroupFilter.customerDuration,
    customer_type: customerGroupFilter.customerType,
    electricity_filter: customerGroupFilter.electricityFilter,
    gas_filter: customerGroupFilter.gasFilter,
    heating_filter: customerGroupFilter.heatingFilter,
    glass_fiber_filter: customerGroupFilter.glassFiberFilter,
    max_churn: customerGroupFilter.maxChurn,
    max_churn_delta: customerGroupFilter.maxChurnDelta,
    max_clv: customerGroupFilter.maxClv,
    min_churn: customerGroupFilter.minChurn,
    min_churn_delta: customerGroupFilter.minChurnDelta,
    min_clv: customerGroupFilter.minClv,
    next_best_activity_filter: customerGroupFilter.nextBestActivityFilter,
    polygon_map_selection: customerGroupFilter.polygonMapSelection
      .map((selection) => `|${selection.id}-${selection.polygon}|`)
      .join(","),
    opt_in: customerGroupFilter.optIn,
    phone: customerGroupFilter.phone,
    email: customerGroupFilter.email,
    salutation: customerGroupFilter.salutation,
    housingSituationsFilter: customerGroupFilter.housingSituationsFilter,
    filterGroupsEnabled: customerGroupFilter.filterGroupsEnabled,
  };

  const filter = opts.saveCustomerGroup
    ? filterForQueryCustomerGroupsSave
    : filterForQuery;
  return opts.omit ? _.omit(filter, opts.omit) : filter;
};



// WEBPACK FOOTER //
// ./src/api/utilities.tsx